// src/pages/shipping/ShippingBulkLabelsDownload.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  UploadCloud,
  DownloadCloud,
  XCircle,
  Hash as HashIcon,
  ScanLine,
  Package,
  MapPin,
  UserCircle2,
  Zap,
  Filter,
} from "lucide-react";
import { API } from "../utils/apiUrls";
import { getOrderStatusName } from "../utils/enums";

const ALLOWED_MANIFEST_STATUS_CODES = Object.freeze([
  "270", // AWB_REGISTERED
  "30",  // ORDER_PLACED
  "40",  // PICKUP_PENDING
  "240", // OUT_FOR_PICKUP
  "50",  // PICKUP_FAILED
]);

const getAuth = () => {
  const Authorization = localStorage.getItem("authToken");
  const merchantId = localStorage.getItem("merchantId");
  if (!Authorization || !merchantId) throw new Error("Not authenticated");
  return { Authorization, merchantId };
};

const formatToISTISO = (d, endOfDay = false) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const time = endOfDay ? "23:59:59" : "00:00:00";
  return `${yyyy}-${mm}-${dd}T${time}+05:30`;
};

const parseApiError = (err) => {
  const data = err?.response?.data || {};
  const status = err?.response?.status;
  const code = data.errorCode || data.error_code || "UNEXPECTED_ERROR";
  const msg =
    data.message ||
    data.error_message ||
    (status === 404
      ? "The requested resource was not found."
      : "Something went wrong. Please try again or contact support.");
  return { title: "Request failed", message: msg, code, status };
};

// EXACTLY same svcSearchOrders style as PickupManifest
async function svcSearchOrders(payload) {
  const { Authorization, merchantId } = getAuth();
  const { data } = await axios.post(API.searchOrders(), payload, {
    headers: {
      Authorization,
      "x-merchant-id": merchantId,
      "Content-Type": "application/json",
    },
  });
  return data;
}

// Stable key for a row
const rowKeyOf = (r, fallbackIndex) =>
  r.order_reference_number ||
  r.awb_number ||
  r.shipment_order_id ||
  r.shipment_order_number ||
  `row-${fallbackIndex}`;

const ShippingBulkLabelsDownload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ───────────────────────────────
  // Auto Search Orders state
  // ───────────────────────────────
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const [query, setQuery] = useState({
    orderReferenceNumber: "",
    awbNumber: "",
    createdRange: [oneMonthAgo, today],
    limit: 50,
    skip: 0,
  });

  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersError, setOrdersError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [total, setTotal] = useState(0);

  const merchantId = localStorage.getItem("merchantId");
  const authToken = localStorage.getItem("authToken");

  // env configs (same as AllShipments)
  const POCAS = process.env.REACT_APP_POCAS_BASE_URL;
  const MERGED_LABELS_PATH =
    process.env.REACT_APP_BULK_MERGED_LABEL_DOWNLOAD_BY_COMMA_SEPARATED_ORDER_REFERENCE_NUMBER;

  // client-side pickup pincode filter
  const [pickupPincodeInput, setPickupPincodeInput] = useState("");
  const [pickupPincodeFilter, setPickupPincodeFilter] = useState("");

  // selection state
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const headerCheckboxRef = useRef(null);
  const [loadingDownloadSelected, setLoadingDownloadSelected] = useState(false);

  // NEW: ref for hidden file input
  const fileInputRef = useRef(null);

  // FILTER → call searchOrders
  // payload is SAME as PickupManifest
  const fetchOrders = async () => {
    try {
      setHasSearched(true);
      setOrdersLoading(true);
      setOrdersError(null);
      const [from, to] = query.createdRange || [oneMonthAgo, today];

      const payload = {
        merchant_id: merchantId,
        order_reference_number: query.orderReferenceNumber || null,
        awb_number: query.awbNumber || null,
        payment_mode: null,
        customer_email: null,
        customer_phone: null,
        order_status: ALLOWED_MANIFEST_STATUS_CODES,
        from_shipment_order_creation_datetime_utc: formatToISTISO(from, false),
        to_shipment_order_creation_datetime_utc: formatToISTISO(to, true),
        limit: query.limit,
        skip: query.skip,
        sort_field: "shipment_order_date_time_utc",
        sort_direction: "DESC",
      };

      const data = await svcSearchOrders(payload);
      setOrders(Array.isArray(data.orders) ? data.orders : []);
      setTotal(data.totalCount ?? 0);
      setSelectedKeys(new Set()); // clear selection when data changes
    } catch (err) {
      console.error(err);
      setOrders([]);
      setTotal(0);
      setOrdersError(parseApiError(err));
      toast.error("Failed to fetch orders");
    } finally {
      setOrdersLoading(false);
    }
  };

  // Auto-call on mount and when pagination changes
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.skip, query.limit]);

  // ───────────────────────────────
  // Bulk Labels Upload
  // ───────────────────────────────
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
  };

  const triggerFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUpload = async () => {
    if (!file) return toast.error("Select a CSV first");

    const apiUrl = `https://pocas.api.wayuvega.in/v1/pocas/label/pdf/download/${merchantId}`;
    const formData = new FormData();
    formData.append("awbCsvMultipartFile", file);

    try {
      setUploading(true);
      const { data } = await axios.post(apiUrl, formData, {
        headers: {
          Authorization: authToken,
          "x-merchant-id": merchantId,
          "Content-Type": "multipart/form-data",
        },
      });

      const fileUrl = data.label_csv_file_url;
      if (!fileUrl) {
        throw new Error("No download URL returned");
      }

      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Labels downloaded!");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Download failed"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSampleDownload = () => {
    const link = document.createElement("a");
    link.href = "/multi_label_download_sample.csv";
    link.setAttribute("download", "multi_label_download_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.max(1, Math.ceil(total / (query.limit || 1)));
  const currentPage = Math.floor(query.skip / query.limit) + 1;

  // apply client-side pickup pincode filter (on current page only)
  const visibleOrders = pickupPincodeFilter.trim()
    ? orders.filter((o) =>
        String(o.pickup_pincode ?? "")
          .trim()
          .includes(pickupPincodeFilter.trim())
      )
    : orders;

  // selection helpers
  const isSelected = (r, idx) => selectedKeys.has(rowKeyOf(r, idx));

  const pageSelectedCount = visibleOrders.filter((r, idx) =>
    isSelected(r, idx)
  ).length;

  const allSelectedOnPage =
    visibleOrders.length > 0 &&
    visibleOrders.every((r, idx) => isSelected(r, idx));

  const toggleRowSelect = (row, idx) => {
    const key = rowKeyOf(row, idx);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAllOnPage = (checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        visibleOrders.forEach((r, idx) => next.add(rowKeyOf(r, idx)));
      } else {
        visibleOrders.forEach((r, idx) => next.delete(rowKeyOf(r, idx)));
      }
      return next;
    });
  };

  useEffect(() => {
    const cb = headerCheckboxRef.current;
    if (!cb) return;
    cb.indeterminate = pageSelectedCount > 0 && !allSelectedOnPage;
  }, [pageSelectedCount, allSelectedOnPage, visibleOrders.length]);

  // pickup pincode filter actions
  const applyPickupFilter = () => {
    setPickupPincodeFilter(pickupPincodeInput.trim());
  };

  const resetPickupFilter = () => {
    setPickupPincodeInput("");
    setPickupPincodeFilter("");
  };

  // ───────────────────────────────
  // Download Label (same behaviour as AllShipments)
  // ───────────────────────────────
  const downloadShipmentLabelsServer = async (orderRefs) => {
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!authToken || !merchantId || !POCAS || !MERGED_LABELS_PATH) {
      throw new Error("Missing config/env for merged label API");
    }
    const apiUrl = `${POCAS}${MERGED_LABELS_PATH}${merchantId}`;
    const payload = orderRefs.map((ref) => ({ order_reference_number: ref }));
    const { data } = await axios.post(apiUrl, payload, {
      headers: { Authorization: authToken, "Content-Type": "application/json" },
    });
    const url = data?.label_csv_file_url;
    if (!url) throw new Error("No label url in response");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadSelectedLabels = async () => {
    const selectedOnPage = visibleOrders.filter((r, idx) =>
      isSelected(r, idx)
    );

    if (selectedOnPage.length === 0) {
      alert("No rows selected.");
      return;
    }

    const orderRefs = selectedOnPage
      .map((s) => s.order_reference_number)
      .filter(Boolean);

    if (orderRefs.length === 0) {
      alert("Selected rows do not have order reference numbers.");
      return;
    }

    setLoadingDownloadSelected(true);
    try {
      // 1) Try merged-label API (same as AllShipments)
      await downloadShipmentLabelsServer(orderRefs);
    } catch (err) {
      console.warn(
        "Merged label API failed; falling back to individual label URLs.",
        err
      );

      // 2) Fallback – open each order_label_url if present
      const withLabels = selectedOnPage.filter((s) => !!s.order_label_url);
      if (withLabels.length === 0) {
        alert("Selected rows have no label URLs yet.");
      } else {
        withLabels.forEach((s, idx) => {
          const a = document.createElement("a");
          a.href = s.order_label_url;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.download = `${
            s.awb_number || s.order_reference_number || "label"
          }.pdf`;
          document.body.appendChild(a);
          setTimeout(() => {
            a.click();
            document.body.removeChild(a);
          }, idx * 200);
        });
        const skipped = selectedOnPage.length - withLabels.length;
        if (skipped > 0) {
          setTimeout(() => {
            alert(
              `${skipped} selected ${
                skipped === 1 ? "row has" : "rows have"
              } no label and were skipped.`
            );
          }, withLabels.length * 200 + 100);
        }
      }
    } finally {
      setLoadingDownloadSelected(false);
    }
  };

  // ───────────────────────────────
  // UI – adaptive layout hugging sidebar & header
  // ───────────────────────────────
  return (
    <div className="w-full min-h-[calc(100vh-88px)] bg-slate-50 px-2">
      {/* full-width content aligned with sidebar (no left padding) & spaced below header */}
      <div className="w-full h-full pl-0 pr-3 sm:pr-4 md:pr-6 lg:pr-8 pt-2 md:pt-2 pb-4 space-y-3 px-2">
        {/* Header */}
        <div className="flex flex-col gap-1 min-w-0">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            Bulk Labels Download
          </h3>
          <p className="text-[0.7rem] md:text-xs text-slate-600 max-w-3xl">
            Upload a CSV of AWB numbers to generate labels in bulk and manage
            label downloads directly from your pickup-ready shipments.
          </p>
        </div>

        {/* Bulk upload bar */}
        <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm px-3 sm:px-4 py-3 flex flex-wrap items-center gap-3">
          {/* Left: description */}
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <UploadCloud className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">
                Upload AWB CSV
              </p>
              <p className="text-[0.7rem] text-slate-500">
                First column should contain AWB numbers. Header row is optional.
              </p>
            </div>
          </div>

          {/* Middle: modern file picker */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 min-w-[230px] max-w-full">
            <button
              type="button"
              onClick={triggerFileDialog}
              className="group flex items-center gap-3 px-3 py-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50 transition cursor-pointer w-full sm:w-auto"
            >
              <div className="h-7 w-7 rounded-md bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center flex-shrink-0">
                <UploadCloud className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-xs font-semibold text-slate-800">
                  {file ? "Change AWB CSV" : "Choose AWB CSV"}
                </span>
                <span className="text-[0.65rem] text-slate-500">
                  {file
                    ? "Click to replace selected file"
                    : "CSV file, up to 5 MB"}
                </span>
              </div>
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />

            {file && (
              <div className="flex items-center gap-2 max-w-xs sm:max-w-sm">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-slate-100 text-[0.7rem] text-slate-700 truncate">
                  <span className="truncate">{file.name}</span>
                  {typeof file.size === "number" && (
                    <span className="text-slate-400">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-red-500 hover:text-red-600 flex-shrink-0"
                  title="Clear file"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-2 justify-end flex-wrap">
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg shadow-sm transition ${
                !file || uploading
                  ? "bg-blue-300 text-white cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              <UploadCloud className="w-4 h-4" />
              {uploading ? "Processing…" : "Upload & Download"}
            </button>

            <button
              onClick={handleSampleDownload}
              className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200"
            >
              <DownloadCloud className="w-4 h-4" />
              Sample CSV
            </button>
          </div>
        </div>

        {/* Error banner (if any) */}
        {ordersError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            <strong className="font-semibold">
              {ordersError.title || "Error"}:
            </strong>{" "}
            {ordersError.message}
          </div>
        )}

        {/* Orders table – auto-loaded on mount */}
        {(ordersLoading || hasSearched) && (
          <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
            {/* Top toolbar: pickup filter + bulk Download Label */}
            <div className="px-3 sm:px-4 pt-3 pb-3 border-b border-slate-100 flex flex-wrap items-end gap-3 justify-between text-xs">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col">
                  <label className="mb-1 text-slate-600 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    Pickup Pincode
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={pickupPincodeInput}
                      onChange={(e) => setPickupPincodeInput(e.target.value)}
                      placeholder="e.g. 560001"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none min-w-[130px]"
                    />
                    <button
                      onClick={applyPickupFilter}
                      className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                    >
                      <Filter className="w-3.5 h-3.5 mr-1" />
                      Search
                    </button>
                    <button
                      onClick={resetPickupFilter}
                      className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-slate-100 text-slate-800 font-semibold hover:bg-slate-200 transition"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="text-slate-500">
                  Selected:{" "}
                  <span className="font-semibold text-slate-900">
                    {pageSelectedCount}
                  </span>
                </div>
                <button
                  onClick={handleDownloadSelectedLabels}
                  disabled={pageSelectedCount === 0 || loadingDownloadSelected}
                  className={`inline-flex items-center justify-center px-3 py-2 rounded-lg text-white font-semibold transition ${
                    pageSelectedCount === 0 || loadingDownloadSelected
                      ? "bg-indigo-400/60 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                  aria-busy={loadingDownloadSelected}
                  title={
                    pageSelectedCount === 0
                      ? "Select at least one row"
                      : "Download labels for selected orders"
                  }
                >
                  {loadingDownloadSelected ? (
                    <>
                      <span className="inline-block w-3 h-3 rounded-full border border-white border-t-transparent animate-spin mr-1" />
                      Preparing…
                    </>
                  ) : (
                    <>
                      <DownloadCloud className="w-3.5 h-3.5 mr-1" />
                      Download Label
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Table area – height-bounded for any zoom */}
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-260px)]">
              <table className="w-full text-[0.8rem] border-collapse">
                <thead className="bg-slate-50 text-slate-600 uppercase text-[0.7rem] tracking-wide sticky top-0 z-10">
                  <tr>
                    <th className="p-2 w-8 text-left">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded-sm accent-blue-600 cursor-pointer"
                        checked={allSelectedOnPage}
                        onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
                        title="Select all on this page"
                      />
                    </th>
                    <th className="p-2 text-left">
                      <div className="inline-flex items-center gap-1">
                        <HashIcon className="w-3 h-3" />
                        Order Ref
                      </div>
                    </th>
                    <th className="p-2 text-left">
                      <div className="inline-flex items-center gap-1">
                        <ScanLine className="w-3 h-3" />
                        AWB
                      </div>
                    </th>
                    <th className="p-2 text-left">
                      <div className="inline-flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        Courier
                      </div>
                    </th>
                    <th className="p-2 text-left">
                      <div className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        Pickup
                      </div>
                    </th>
                    <th className="p-2 text-left">
                      <div className="inline-flex items-center gap-1">
                        <UserCircle2 className="w-3 h-3" />
                        Delivery
                      </div>
                    </th>
                    <th className="p-2 text-left">
                      <div className="inline-flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        Status
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!ordersLoading && visibleOrders.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center text-slate-500 text-xs py-8"
                      >
                        {orders.length === 0
                          ? "No orders found for current criteria."
                          : "No orders match this pickup pincode."}
                      </td>
                    </tr>
                  )}

                  {visibleOrders.map((r, idx) => {
                    const createdDisplay = r.shipment_order_date_time_utc
                      ? new Date(
                          r.shipment_order_date_time_utc
                        ).toLocaleString("en-IN", {
                          timeZone: "Asia/Kolkata",
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : r.order_placed_date_time || "—";

                    const checked = isSelected(r, idx);

                    return (
                      <tr
                        key={rowKeyOf(r, idx)}
                        className="border-t border-slate-100 hover:bg-blue-50/40 transition-colors"
                      >
                        <td className="p-2 align-top">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded-sm accent-blue-600 cursor-pointer mt-1"
                            checked={checked}
                            onChange={() => toggleRowSelect(r, idx)}
                            title={checked ? "Unselect" : "Select"}
                          />
                        </td>
                        <td className="p-2 align-top">
                          <div className="flex flex-col">
                            <span className="font-medium text-[0.8rem] text-slate-900">
                              {r.order_reference_number}
                            </span>
                            <span className="text-[0.7rem] text-slate-500">
                              {createdDisplay}
                            </span>
                          </div>
                        </td>
                        <td className="p-2 align-top text-[0.8rem] text-slate-800">
                          {r.awb_number || "—"}
                        </td>
                        <td className="p-2 align-top text-[0.8rem] text-slate-800">
                          {r.allocated_carrier_partner_public_name || "—"}
                        </td>
                        <td className="p-2 align-top text-[0.8rem]">
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-900">
                              {r.pickup_organisation}
                            </span>
                            <span className="text-[0.7rem] text-slate-500">
                              {r.pickup_city} — {r.pickup_pincode}
                            </span>
                          </div>
                        </td>
                        <td className="p-2 align-top text-[0.8rem]">
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-900">
                              {r.drop_name}
                            </span>
                            <span className="text-[0.7rem] text-slate-500">
                              {r.drop_city}, {r.drop_state} — {r.drop_pincode}
                            </span>
                          </div>
                        </td>
                        <td className="p-2 align-top text-[0.8rem]">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[0.7rem] font-medium">
                            {getOrderStatusName(r.latest_order_status_code)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}

                  {ordersLoading && (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center text-slate-500 text-xs py-8"
                      >
                        Loading…
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom pagination + count */}
            <div className="px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-slate-100 text-xs text-slate-600 bg-slate-50/70">
              <div>
                Showing{" "}
                <span className="font-semibold text-slate-900">
                  {visibleOrders.length}
                </span>{" "}
                orders
              </div>
              <div className="flex items-center gap-2">
                <span>
                  Page{" "}
                  <span className="font-semibold text-slate-900">
                    {currentPage}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-slate-900">
                    {totalPages}
                  </span>
                </span>
                <button
                  onClick={() =>
                    setQuery((prev) => ({
                      ...prev,
                      skip: Math.max(0, prev.skip - prev.limit),
                    }))
                  }
                  disabled={query.skip === 0 || ordersLoading}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-xs"
                >
                  ← Prev
                </button>
                <button
                  onClick={() =>
                    orders.length === query.limit &&
                    setQuery((prev) => ({
                      ...prev,
                      skip: prev.skip + prev.limit,
                    }))
                  }
                  disabled={orders.length < query.limit || ordersLoading}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-xs"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingBulkLabelsDownload;
